import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { CircularProgress, TextField } from "@mui/material";
import { FaArrowLeft, FaLock, FaWhatsapp } from "react-icons/fa";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { validatePhone } from "components/PhoneInput/countries";
import { AuthService } from "services/authService";
import whatsappService from "services/admin/whatsappService";
import { loginSuccessAction } from "redux/actions/authActions";
import { ADMIN_HOME_PATH } from "helpers/url";
import { OnboardLayout } from "components/OnboardLayout/OnboardLayout";
import {
  AI_AGENT_FEATURE,
  AuthFeatures,
  AuthIllustration,
  CHATBOT_FEATURE,
  DEMO_DATA_FEATURE,
  ErrorBox,
  FieldLabel,
  PRIMARY_BUTTON_CLASS,
} from "../components/AuthDecor";

// Тестовый номер: после кода страны идёт 0 (+7 000 ...). Такой номер не может быть реальным,
// поэтому код в WhatsApp не отправляем и сразу создаём демо-организацию. Та же проверка — в trial.service на бэкенде.
// Только для этих стран: в некоторых других (например, Италия) реальные номера начинаются с 0
const TEST_PHONE_COUNTRY_CODES = ["7", "971", "966"];
const isTestPhone = (phone, countryCode) =>
  TEST_PHONE_COUNTRY_CODES.includes(String(countryCode)) && String(phone || "").startsWith(`${countryCode}0`);

const MIN_PASSWORD_LENGTH = 6;
const OTP_LENGTH = 6;

const validateText = (value, emptyMessage) => {
  const text = value.trim();
  if (!text) return emptyMessage;
  if (text.length < 2) return "Слишком короткое значение";
  if (text.length > 100) return "Не длиннее 100 символов";
  return null;
};

/** Ошибки шага с данными компании: { поле: текст ошибки } */
const validateDetails = (form) => {
  const errors = {
    name: validateText(form.name, "Введите ваше имя"),
    orgName: validateText(form.orgName, "Введите название компании"),
    phone: validatePhone(form.phone, form.phonePrefix),
    password: !form.password
      ? "Придумайте пароль"
      : form.password.length < MIN_PASSWORD_LENGTH
        ? `Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов`
        : null,
  };
  return Object.fromEntries(Object.entries(errors).filter(([, message]) => message));
};

const validateCode = (code) => {
  if (!code) return "Введите код из WhatsApp";
  if (code.length !== OTP_LENGTH) return `Код состоит из ${OTP_LENGTH} цифр`;
  return null;
};

/**
 * Демо-доступ: регистрация компании с пробным периодом (7 дней, срок задаёт тариф Trial).
 * Шаг 1 — данные компании и телефон, отправляем код в WhatsApp.
 * Шаг 2 — вводим код, создаём организацию с демо-данными и сразу входим.
 */
export const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({ name: "", orgName: "", phone: "", phonePrefix: 7, password: "" });
  const [code, setCode] = useState("");
  // Локальная разработка: бэкенд с WHATSAPP_OTP_DISABLED=true не отправляет код, а возвращает его
  const [devCode, setDevCode] = useState(null);
  const [step, setStep] = useState("details");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Ошибки полей показываем после ухода с поля или после попытки отправить форму
  const [touched, setTouched] = useState({});
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [codeSubmitted, setCodeSubmitted] = useState(false);

  const detailsErrors = validateDetails(form);
  const fieldError = (field) => ((detailsSubmitted || touched[field]) && detailsErrors[field]) || null;
  const touch = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));
  const codeError = validateCode(code);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const getErrorMessage = (ex, fallback) =>
    ex?.response?.data?.error?.message || ex?.response?.data?.message || ex?.message || fallback;

  const testPhone = isTestPhone(form.phone, form.phonePrefix);

  const sendCode = async (e) => {
    e.preventDefault();
    setError(null);
    setDetailsSubmitted(true);
    if (Object.keys(detailsErrors).length) {
      return;
    }
    if (testPhone) {
      await createTrial("");
      return;
    }
    setLoading(true);
    try {
      const { data } = await whatsappService.singin(form.phone);
      if (data?.devCode) {
        setDevCode(data.devCode);
        setCode(data.devCode);
      }
      setStep("code");
    } catch (ex) {
      setError(getErrorMessage(ex, "Не удалось отправить код. Попробуйте позже"));
    } finally {
      setLoading(false);
    }
  };

  const signup = async (e) => {
    e.preventDefault();
    setCodeSubmitted(true);
    if (codeError) {
      return;
    }
    await createTrial(code);
  };

  const createTrial = async (otpCode) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await AuthService.trialSignup({
        name: form.name.trim(),
        orgName: form.orgName.trim(),
        phone: form.phone,
        phonePrefix: form.phonePrefix,
        password: form.password,
        code: otpCode,
      });
      localStorage.setItem("orgID", data.orgID);
      dispatch(loginSuccessAction({ user: data.user, token: data.token }));
      navigate(ADMIN_HOME_PATH);
    } catch (ex) {
      setError(getErrorMessage(ex, "Не удалось зарегистрироваться. Попробуйте позже"));
    } finally {
      setLoading(false);
    }
  };

  const backToDetails = () => {
    setStep("details");
    setError(null);
    setCodeSubmitted(false);
  };

  return (
    <>
      <Helmet>
        <title>Демо-доступ | Ubee AI</title>
        <meta name="description" content="Попробуйте платформу Ubee AI бесплатно 7 дней" />
      </Helmet>
      <OnboardLayout
        title="7 дней бесплатно"
        subtitle="Полный доступ к Ubee AI, включая ИИ-агента"
        illustration={<AuthIllustration />}
        features={<AuthFeatures items={[CHATBOT_FEATURE, AI_AGENT_FEATURE, DEMO_DATA_FEATURE]} />}
      >
        <div className="inline-block rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-[#4a2366] mb-4">
          {testPhone ? "Тестовый номер" : `Шаг ${step === "details" ? 1 : 2} из 2`}
        </div>

        {step === "details" && (
          <>
            <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-2">Создайте кабинет</h2>
            <p className="text-gray-500 mb-8">Займёт минуту. Мы сразу заполним кабинет демо-данными</p>

            <form onSubmit={sendCode} noValidate className="flex flex-col gap-4">
              <div>
                <FieldLabel>Ваше имя</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="Например, Айгерим"
                  value={form.name}
                  onChange={setField("name")}
                  onBlur={touch("name")}
                  error={Boolean(fieldError("name"))}
                  helperText={fieldError("name")}
                  inputProps={{ maxLength: 100 }}
                  autoComplete="name"
                />
              </div>
              <div>
                <FieldLabel>Название компании</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="Например, Sunrise Travel"
                  value={form.orgName}
                  onChange={setField("orgName")}
                  onBlur={touch("orgName")}
                  error={Boolean(fieldError("orgName"))}
                  helperText={fieldError("orgName")}
                  inputProps={{ maxLength: 100 }}
                  autoComplete="organization"
                />
              </div>
              <div>
                <FieldLabel>Номер WhatsApp</FieldLabel>
                <PhoneInput
                  phoneNumber={form.phone}
                  countryCode={form.phonePrefix}
                  hideLabel={true}
                  error={fieldError("phone")}
                  onBlur={touch("phone")}
                  onChange={(params) => {
                    setForm((prev) => ({
                      ...prev,
                      ...(params.countryCode ? { phonePrefix: params.countryCode } : {}),
                      ...(params.phone !== undefined ? { phone: params.phone } : {}),
                    }));
                  }}
                />
                {!fieldError("phone") && (
                  <div className="text-xs text-gray-500 mt-1.5 mx-3.5">Пришлём код подтверждения. Этот номер — логин для входа</div>
                )}
              </div>
              <div>
                <FieldLabel>Пароль для входа</FieldLabel>
                <TextField
                  fullWidth
                  type="password"
                  placeholder={`Не короче ${MIN_PASSWORD_LENGTH} символов`}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={setField("password")}
                  onBlur={touch("password")}
                  error={Boolean(fieldError("password"))}
                  helperText={fieldError("password")}
                />
              </div>

              {testPhone && (
                <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-gray-700">
                  <b>Тестовый номер.</b> Код в WhatsApp не отправляется — кабинет создастся сразу.
                </div>
              )}
              {error && <ErrorBox>{error}</ErrorBox>}

              <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
                {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : !testPhone && <FaWhatsapp size={22} />}
                {testPhone
                  ? loading ? "Готовим кабинет…" : "Создать демо-кабинет"
                  : loading ? "Отправляем код…" : "Получить код в WhatsApp"}
              </button>
            </form>
          </>
        )}

        {step === "code" && (
          <>
            <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-2">Подтвердите номер</h2>
            <p className="text-gray-500 mb-6">Введите 6 цифр из сообщения</p>

            <div className="flex items-center gap-3 rounded-xl border px-4 py-3 mb-6">
              <span className="shrink-0 w-9 h-9 rounded-lg bg-[#25d366] text-white flex items-center justify-center">
                <FaWhatsapp size={20} />
              </span>
              {devCode ? (
                <span className="text-sm text-orange-600">Dev-режим: код не отправлялся в WhatsApp и подставлен автоматически</span>
              ) : (
                <span className="text-sm text-gray-700">
                  Отправили код в WhatsApp на номер <b>+{form.phone}</b>
                </span>
              )}
            </div>

            <form onSubmit={signup} noValidate className="flex flex-col gap-4">
              <div>
                <FieldLabel>Код подтверждения</FieldLabel>
                <TextField
                  fullWidth
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
                  error={codeSubmitted && Boolean(codeError)}
                  helperText={codeSubmitted ? codeError : null}
                  inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: OTP_LENGTH }}
                  sx={{ "& input": { textAlign: "center", fontSize: 28, fontWeight: 600, letterSpacing: "0.5em" } }}
                  autoFocus
                />
              </div>

              {error && <ErrorBox>{error}</ErrorBox>}

              <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
                {loading && <CircularProgress size={22} sx={{ color: "white" }} />}
                {loading ? "Готовим кабинет…" : "Начать пробный период"}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={backToDetails}
                className="inline-flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 disabled:opacity-60"
              >
                <FaArrowLeft size={13} />
                Изменить данные
              </button>
            </form>
          </>
        )}

        <div className="mt-8 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3 text-sm text-gray-700">
          Уже есть аккаунт?{" "}
          <Link to="/auth/login" className="font-semibold text-[#4a2366] hover:underline">
            Войти
          </Link>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400">
          <FaLock size={11} />
          <span>Данные передаются по защищённому соединению</span>
        </div>
      </OnboardLayout>
    </>
  );
};
